<script setup lang="ts">
import { nextTick, ref, watch } from 'vue';
import type { CaregiverShift, CustomShiftRange } from '@/api/shifts';

interface Header {
  title: string;
  value: string;
  fixed?: boolean;
  nowrap?: boolean;
  headerProps?: { class: string };
  cellProps?: { class: string };
}

// One grid row: a fixed slot (day, night, 24 horas) or one custom range, plus
// a checked flag per day column.
interface ShiftItem {
  shiftType: string;
  name: string;
  startTime?: string;
  endTime?: string;
  [key: `day${number}`]: boolean;
  [key: string]: string | boolean | undefined;
}

const props = defineProps({
  firstDay: {
    type: String,
    required: true,
  },
  caregiverId: {
    type: Number,
    required: true,
  },
  blockedShifts: {
    type: Array<CaregiverShift>,
    default: () => [],
  },
  showShifts: {
    type: Array<boolean>,
    default: () => [true, true, true, true],
  },
  customRanges: {
    type: Array<CustomShiftRange>,
    default: () => [],
  },
});

const days = 7;
const currentDay = ref(1);
const lastDay = ref(days);
// The slot column stays pinned while the table scrolls sideways, so every
// added week still shows which slot each checkbox belongs to.
function slotHeader(): Header {
  return { title: 'Turno', value: 'name', fixed: true, nowrap: true };
}

const headers = ref<Header[]>([slotHeader()]);
const table = ref<{ $el: HTMLElement } | null>(null);

function checkIfIsWeekend(date: Date) {
  const SATURDAY = 6;
  const SUNDAY = 0;

  return date.getDay() === SATURDAY || date.getDay() === SUNDAY;
}

function generateDayHeaders() {
  const INITIAL_DAY_OFFSET = 2;

  for (let day = 1; day <= days; day++) {
    const dayDate = new Date(props.firstDay);
    const dayOffset = currentDay.value + day - INITIAL_DAY_OFFSET;
    dayDate.setDate(dayDate.getDate() + dayOffset);

    const dayTitle = dayDate.toLocaleDateString('es', { weekday: 'short', day: 'numeric', month: 'numeric' });
    headers.value.push(
      {
        title: dayTitle,
        value: `day${dayOffset + 1}`,
        headerProps: { class: checkIfIsWeekend(dayDate) ? 'bg-blue-grey-lighten-4' : '' },
        cellProps: { class: checkIfIsWeekend(dayDate) ? 'bg-blue-grey-lighten-4' : '' },
      });
  }
}

watch(lastDay, () => {
  generateDayHeaders();
});

const items = ref<ShiftItem[]>([]);

function customRangeName(range: CustomShiftRange) {
  return `Personalizado ${range.startTime} - ${range.endTime}`;
}

function setItems() {
  if (props.showShifts[0]) items.value.push({ shiftType: 'day', name: 'Día' });
  if (props.showShifts[1]) items.value.push({ shiftType: 'night', name: 'Noche' });
  // eslint-disable-next-line no-magic-numbers
  if (props.showShifts[2]) items.value.push({ shiftType: '24', name: '24 horas' });
  // eslint-disable-next-line no-magic-numbers
  if (!props.showShifts[3]) return;

  props.customRanges.forEach((range) => {
    items.value.push({
      shiftType: 'custom', name: customRangeName(range), startTime: range.startTime, endTime: range.endTime,
    });
  });
}
setItems();

function addDaysToItems() {
  items.value.forEach(shiftItem => {
    for (let day = 1; day <= lastDay.value; day++) {
      if (shiftItem[`day${day}`] !== undefined) continue;
      shiftItem[`day${day}`] = false;
    }
  });
}

function rowKey(item: ShiftItem): string {
  return item.shiftType === 'custom' ? `custom:${item.startTime}-${item.endTime}` : item.shiftType;
}

// Rows are rebuilt when the batch type or the range list changes; a row that
// survives (same slot, or same range) keeps its checked days, so adding one
// more range in step 2 does not clear the grids already filled in.
function rebuildItems() {
  const previous = new Map(items.value.map(item => [rowKey(item), item]));
  items.value = [];
  setItems();
  items.value.forEach((item) => {
    const before = previous.get(rowKey(item));
    if (!before) return;
    for (let day = 1; day <= lastDay.value; day++) item[`day${day}`] = Boolean(before[`day${day}`]);
  });
  addDaysToItems();
}

watch([() => props.showShifts, () => props.customRanges], rebuildItems, { deep: true });

async function nextWeek() {
  currentDay.value += days;
  lastDay.value += days;
  addDaysToItems();
  await nextTick();
  const wrapper = table.value?.$el.querySelector('.v-table__wrapper');
  wrapper?.scrollTo({ left: wrapper.scrollWidth, behavior: 'smooth' });
}

const caregiverShifts = defineModel<CaregiverShift[]>({ default: () => [] });

const shiftsCount = ref(0);

function shiftsOn(shift: CaregiverShift): number {
  return [shift.dayShift, shift.nightShift, shift.customShift].filter(Boolean).length +
    (shift.customShifts?.length ?? 0);
}

function setShiftsCount() {
  shiftsCount.value = caregiverShifts.value.reduce((count, shift) => count + shiftsOn(shift), 0);
}

const emit = defineEmits(['input']);

function getShiftValue(shiftType: string, dayKey: string): boolean {
  const item = items.value.find((i) => i.shiftType === shiftType);

  return Boolean(item?.[dayKey]);
}

function checkedCustomRanges(dayKey: string): CustomShiftRange[] {
  return items.value
    .filter(item => item.shiftType === 'custom' && item[dayKey])
    .map(item => ({ startTime: item.startTime ?? '', endTime: item.endTime ?? '' }));
}

function buildCaregiverShift(dayNumber: number): CaregiverShift {
  const date = new Date(props.firstDay);
  date.setDate(new Date(props.firstDay).getDate() + dayNumber - 1);
  const dayKey = `day${dayNumber}`;

  return {
    day: date.toISOString().split('T')[0] ?? '',
    dayShift: getShiftValue('day', dayKey),
    nightShift: getShiftValue('night', dayKey),
    customShift: getShiftValue('24', dayKey),
    customShifts: checkedCustomRanges(dayKey),
  };
}

function save() {
  caregiverShifts.value = Array.from(
    { length: lastDay.value },
    (_, index) => buildCaregiverShift(index + 1),
  );
  setShiftsCount();
  emit('input', caregiverShifts.value);
}

watch(items, () => {
  save();
}, { deep: true });

watch(() => props.firstDay, () => {
  headers.value = [slotHeader()];
  generateDayHeaders();
  addDaysToItems();
}, { deep: true, immediate: true });

function isBlockedRange(blocked: CaregiverShift, item: ShiftItem): boolean {
  return (blocked.customShifts ?? []).some(
    range => range.startTime === item.startTime && range.endTime === item.endTime,
  );
}

// A slot another caregiver of the batch already took on that date; custom rows
// match by their own range, never by row position.
function isBlockedFor(blocked: CaregiverShift, item: ShiftItem): boolean {
  if (item.shiftType === 'day') return blocked.dayShift;
  if (item.shiftType === 'night') return blocked.nightShift;
  if (item.shiftType === '24') return blocked.customShift;

  return isBlockedRange(blocked, item);
}

function isShiftTaken(dayNumber: number, item: ShiftItem): boolean {
  const targetDate = new Date(props.firstDay);
  targetDate.setDate(targetDate.getDate() + dayNumber - 1);
  const blocked = props.blockedShifts.find(
    shift => new Date(shift.day.replace(/-/g, '/')).toDateString() === targetDate.toDateString(),
  );

  return !!blocked && isBlockedFor(blocked, item);
}
</script>
<template>
  <v-data-table
    ref="table"
    :headers="headers"
    :items="items"
    hide-default-footer
  >
    <template
      v-for="day in lastDay"
      #[`item.day${day}`]="{ item }"
      :key="`${Math.random()}day${day}`"
    >
      <v-checkbox
        v-if="isShiftTaken(day, item)"
        :model-value="true"
        disabled
      />
      <v-checkbox
        v-else
        v-model="item[`day${day}`]"
        color="primary"
      />
    </template>
    <template #bottom>
      <v-row
        class="mt-4 ml-2 text-right"
        justify="space-between"
        align="center"
      >
        <p class="mx-4">
          {{ shiftsCount }} turnos asignados
        </p>
        <v-btn
          class="mr-4"
          variant="text"
          color="primary"
          append-icon="mdi-chevron-right"
          @click="nextWeek"
        >
          Siguiente semana
        </v-btn>
      </v-row>
    </template>
  </v-data-table>
</template>
